import * as React from 'react';
import classnames from 'classnames';
import { classes, st } from './BoxSelection.st.css';
import { classes as optionClasses } from './Option.st.css';
import { Option as BoxSelectionOption } from './Option';
import { NativeSelectorProvider } from './NativeSelector';
import {
  BoxSelectionBoxSize,
  BoxSelectionDefaultProps,
  BoxSelectionProps,
  BoxSelectionOptionProps,
  BoxSelectionSelectionType,
  BoxSelectionDataHooks,
} from './types';
import { Fieldset } from '../internal/Fieldset';
import uniqueId from '../../common/uniqueId';

/** The box selection is used to give the user to select single or multiple boxes. */
export class BoxSelection extends React.Component<BoxSelectionProps> {
  static displayName = 'BoxSelection';
  static defaultProps: BoxSelectionDefaultProps = {
    size: BoxSelectionBoxSize.xLarge,
  };
  static Option = BoxSelectionOption;

  private readonly childRefs = [];
  private readonly _errorMessageId: string;

  constructor(props: BoxSelectionProps) {
    super(props);

    this._errorMessageId = uniqueId('error-message');
  }

  focus(index?: number) {
    const refToFocus =
      typeof index === 'number'
        ? this.childRefs[index]
        : this.childRefs.find((ref) => ref.current.props.checked) ||
          this.childRefs[0];
    refToFocus?.current?.focus();
  }

  _getNativeSelector = (): BoxSelectionSelectionType => {
    const { children } = this.props;
    if (!children) {
      return BoxSelectionSelectionType.RADIO_BUTTON;
    }

    const enabledChildren = React.Children.toArray(children).filter(
      (child) =>
        React.isValidElement<BoxSelectionOptionProps>(child) &&
        !child.props.disabled,
    );

    return enabledChildren.length === 1
      ? BoxSelectionSelectionType.CHECKBOX
      : BoxSelectionSelectionType.RADIO_BUTTON;
  };

  render() {
    const {
      className,
      children,
      size,
      name,
      onChange,
      vertical,
      inline,
      error,
      errorMessage,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      label,
      suffix,
      showAsterisk = true,
      required,
    } = this.props;

    const isErrorMessageProvided = error && !!errorMessage;
    const stStates = {
      error,
      hasErrorMessage: !!errorMessage,
    };

    return (
      <Fieldset
        showLabelAsterisk={showAsterisk && required}
        errorMessage={isErrorMessageProvided && errorMessage}
        errorMessageId={this._errorMessageId}
        className={st(classes.root, stStates, className)}
        dataHook={this.props['data-hook']}
        label={label}
      >
        <div
          className={classnames(classes.container, className, {
            [classes.vertical]: vertical,
            [classes.inline]: inline,
          })}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
        >
          <NativeSelectorProvider value={this._getNativeSelector()}>
            {React.Children.map(
              children,
              (
                child: React.ClassicElement<BoxSelectionOptionProps>,
                key: number,
              ) => {
                this.childRefs[key] = child.ref || React.createRef<any>();

                return (
                  React.isValidElement(child) &&
                  React.cloneElement(child, {
                    key,
                    name,
                    size,
                    onChange,
                    vertical,
                    ref: this.childRefs[key],
                    className: st(
                      classes.item,
                      child.props.className,
                      isErrorMessageProvided ? optionClasses.error : '',
                    ),
                    required,
                  })
                );
              },
            )}
            {!!suffix && (
              <div
                className={classes.suffix}
                data-hook={BoxSelectionDataHooks.BOX_SELECTION_SUFFIX}
              >
                {suffix}
              </div>
            )}
          </NativeSelectorProvider>
        </div>
      </Fieldset>
    );
  }
}
